import React from "react";
import PropTypes from "prop-types";
import { Modal } from "components/Modal";
import { Button } from "components/Button";
import styled from "styled-components";
import plaidImage from "../../../assets/images/plaid.png";
import surmountUpgradeLogo from "../../../assets/images/surmount-upgrade-logo.svg";

// Styled components for the modal
const ModalContainer = styled.div`
  padding: 28px;
  width: 100%;
  max-width: 450px;
  background: white;
  border-radius: 12px;
  display: flex;
  flex-direction: column;
  align-items: center;
  position: relative;
  box-sizing: border-box;
`;

const IconsSection = styled.div`
  position: relative;
  width: 80px;
  height: 40px;
  margin-bottom: 24px;
  align-self: flex-start;
  margin-left: 0;
`;

const SurmountIcon = styled.img`
  position: absolute;
  left: 0;
  top: 0;
  width: 40px;
  height: 40px;
  border-radius: 50%;
  z-index: 2;
`;

const PlaidIcon = styled.img`
  position: absolute;
  left: 25px;
  top: 0;
  width: 32px;
  height: 32px;
  border-radius: 50%;
  z-index: 1;
`;

const TitleContainer = styled.div`
  width: 100%;
  align-self: flex-start;
  margin-bottom: 28px;
  box-sizing: border-box;
`;

const Title = styled.h2`
  font-size: 18px;
  font-weight: 600;
  color: #1f2937;
  margin: 0 0 14px 0;
  line-height: 1.3;
  text-align: left;
  font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif;
`;

const Subtitle = styled.h3`
  font-size: 15px;
  font-weight: 600;
  color: #374151;
  margin: 0;
  text-align: left;
  font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif;
`;

const FeatureList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 18px;
  margin-bottom: 28px;
  width: 100%;
  box-sizing: border-box;
`;

const Feature = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 16px;
  text-align: left;
  width: 100%;
  box-sizing: border-box;
`;

const FeatureIcon = styled.div`
  flex-shrink: 0;
  margin-top: 2px;
`;

const FeatureText = styled.span`
  font-size: 15px;
  color: #6b7280;
  line-height: 1.5;
  flex: 1;
  width: 100%;
  text-align: left;
  font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif;
`;

const ButtonContainer = styled.div`
  width: 100%;
  margin-top: 8px;
  display: flex;
  justify-content: center;
  box-sizing: border-box;
`;

const PlaidConnectionModal = ({
  show,
  onHide,
  onConnect,
  isLoading = false,
}) => {
  return (
    <Modal
      show={show}
      onHide={onHide}
      size="auto"
      className="plaid-connection-modal"
      showClose={true}
      centered={true}
    >
      <ModalContainer>
        <IconsSection>
          <SurmountIcon src={surmountUpgradeLogo} alt="Surmount Upgrade" />
          <PlaidIcon src={plaidImage} alt="Plaid" />
        </IconsSection>

        <TitleContainer>
          <Title>We&apos;ll use Plaid to link your bank account</Title>
          <Subtitle>Why we use Plaid</Subtitle>
        </TitleContainer>

        <FeatureList>
          <Feature>
            <FeatureIcon>
              <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
                <circle cx="10" cy="10" r="10" fill="#F3F4F6" />
                <circle cx="10" cy="10" r="6" fill="#9CA3AF" />
                <path
                  d="M7 10l2 2 4-4"
                  stroke="white"
                  strokeWidth="1.5"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
            </FeatureIcon>
            <FeatureText>
              Plaid is a reliable financial service that supports well-known
              finance applications in the U.S.
            </FeatureText>
          </Feature>

          <Feature>
            <FeatureIcon>
              <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
                <circle cx="10" cy="10" r="10" fill="#F3F4F6" />
                <circle cx="10" cy="10" r="6" fill="#9CA3AF" />
                <path
                  d="M7 10l2 2 4-4"
                  stroke="white"
                  strokeWidth="1.5"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
            </FeatureIcon>
            <FeatureText>
              Once your account is connected, you can make deposits anytime
              without needing to log in.
            </FeatureText>
          </Feature>

          <Feature>
            <FeatureIcon>
              <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
                <circle cx="10" cy="10" r="10" fill="#F3F4F6" />
                <circle cx="10" cy="10" r="6" fill="#9CA3AF" />
                <path
                  d="M7 10l2 2 4-4"
                  stroke="white"
                  strokeWidth="1.5"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
            </FeatureIcon>
            <FeatureText>
              It employs cutting-edge encryption technology to safeguard your
              login details, ensuring they are never stored or shared.
            </FeatureText>
          </Feature>
        </FeatureList>

        <ButtonContainer>
          <Button
            onClick={onConnect}
            style={{
              backgroundColor: "#3B82F6",
              color: "white",
              border: "none",
              borderRadius: "8px",
              padding: "12px 24px",
              fontSize: "14px",
              fontWeight: "600",
              cursor: "pointer",
              width: "100%",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              transition: "all 0.2s ease",
              boxShadow: "0 1px 2px 0 rgba(0, 0, 0, 0.05)",
              fontFamily:
                "-apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif",
              height: "44px",
            }}
            disabled={isLoading}
          >
            {isLoading ? "Connecting..." : "Next"}
          </Button>
        </ButtonContainer>
      </ModalContainer>
    </Modal>
  );
};

PlaidConnectionModal.propTypes = {
  show: PropTypes.bool.isRequired,
  onHide: PropTypes.func.isRequired,
  onConnect: PropTypes.func.isRequired,
  isLoading: PropTypes.bool,
};

export default PlaidConnectionModal;
